import { Dispatch, SetStateAction } from 'react'
import { useFormContext } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import {
  CatAndSubCatDefaultCategoryFragment,
  CreateProductInformationDefaultCategoryFragment,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import FormInput from '@/modules/shared/components/create-form/FormInput'
import CatAndSubCat from '@/modules/suppliers/pages/supplier/general/settings/form-elements/CatAndSubCat'

interface CreateProductInformationProps {
  setSelectedCategory: Dispatch<SetStateAction<CatAndSubCatDefaultCategoryFragment | null>>
  defaultCategory?: CreateProductInformationDefaultCategoryFragment | null
  useOwnerCode?: boolean
  externalIdDescription?: string
  removeDefaultCategory?: boolean
  setRemoveDefaultCategory?: Dispatch<SetStateAction<boolean>>
  shouldDirtyOnRemove?: boolean
  categoryDescription?: string
  hideCode?: boolean
}

export default function CreateProductInformation({
  setSelectedCategory,
  defaultCategory,
  useOwnerCode,
  externalIdDescription,
  removeDefaultCategory,
  setRemoveDefaultCategory,
  shouldDirtyOnRemove,
  categoryDescription,
  hideCode,
}: CreateProductInformationProps) {
  const { t } = useTranslation()
  const { formState } = useFormContext()
  const { currentOrganisation } = useSessionUser()

  return (
    <>
      <CatAndSubCat
        title={t('createProduct.categoryTitle', 'Product Category and Sub-Category')}
        description={
          categoryDescription ||
          t(
            'createProduct.categoryDescription',
            'The product Category is required, you must assign a Category and Sub-Category to the product you are creating.'
          )
        }
        register="categoryId"
        setCategoryObject={setSelectedCategory}
        defaultCategory={defaultCategory}
        removeDefaultCategory={removeDefaultCategory || false}
        setRemoveDefaultCategory={setRemoveDefaultCategory}
        shouldDirtyOnRemove={shouldDirtyOnRemove}
      />

      {formState.errors.categoryId && formState.errors.categoryId.type === 'invalid_type' && (
        <span className="mt-2 block text-sm text-error" role="alert">
          {t('createProduct.categoryRequiredMsg', 'You must provide a Category and Sub-Category.')}
        </span>
      )}
      <FormInput
        title={t('general.brand', 'Brand')}
        description={t(
          'createProduct.brandDescription',
          'Provide a Brand for the product if applicable, the Brand is optional.'
        )}
        register="brand"
        placeholder={t('general.brand', 'Brand')}
        className="mt-8"
      />
      <FormInput
        title={t('general.productName', 'Product Name')}
        description={t(
          'createProduct.productNameDescription',
          'Provide a name for the product using this field, a name is required.'
        )}
        register="itemDescription"
        requiredError={t('createProduct.productNameError', 'You must provide a Product Name.')}
        placeholder={t('general.productName', 'Product Name')}
        className="mt-8"
      />
      {useOwnerCode && (
        <FormInput
          title={t('general.ownerCode', 'Owner Code')}
          description={
            <Trans i18nKey="createProduct.ownerCodeDescription">
              The Owner Code is the code that the Organisation who owns this Product uses to identify this Product. This
              Code will be visible to all users at the Organisation or any other Organisation that can view this
              product. The Owner Code is optional and does not need to be unique. If you are creating a Master Product
              please leave this field empty.
            </Trans>
          }
          register="ownerCode"
          placeholder={t('general.ownerCode', 'Owner Code')}
          className="mt-8"
        />
      )}
      {currentOrganisation?.isAPurchaser && !useOwnerCode && !hideCode && (
        <FormInput
          title={t('general.purchaserCode', 'Purchaser Code')}
          description={
            <Trans i18nKey="createProduct.purchaserCodeDescription">
              The Purchaser Code is the code that your organisation uses to search for and identify this product. The
              Purchaser Code is optional and does not need to be unique, although we recommend that it is kept unique.
              The Purchaser Code will only be seen by users at your organisation.
            </Trans>
          }
          register="code"
          placeholder={t('general.purchaserCode', 'Purchaser Code')}
          className="mt-8"
        />
      )}
      {!currentOrganisation?.isAPurchaser && !useOwnerCode && !hideCode && (
        <FormInput
          title={t('general.supplierCode', 'Supplier Code')}
          description={
            <Trans i18nKey="createProduct.supplierCodeDescription">
              The Supplier Code is the code that your organisation uses to search for and identify this product. The
              Supplier Code is optional and does not need to be unique, although we recommend that it is kept unique.
              The Supplier Code will be seen by users at your organisation, and any Purchaser who you allow to view this
              product when placing it in a Catalog.
            </Trans>
          }
          register="code"
          placeholder={t('general.supplierCode', 'Supplier Code')}
          className="mt-8"
        />
      )}
      <FormInput
        title={t('general.externalId', 'External ID')}
        description={
          externalIdDescription ||
          t(
            'createStockItem.externalIdDescription',
            'The External ID can be important for integrating PurchasePlus with an external system. The External ID is the unique identifier that may be used to identify this product in other systems at your Organisation.'
          )
        }
        register="externalId"
        placeholder={t('general.externalId', 'External ID')}
        className="mt-8"
      />
    </>
  )
}
